import * as Sentry from '@sentry/vue'
import axios, { isCancel, AxiosError, HttpStatusCode, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'

import { tokenService } from '@/utils/auth/tokenService'
import { redactSensitive } from '@/utils/monitoring/redactSensitive'

import * as aggr from '@/api-generator/aggr/index'
import * as oidc from '@/api-generator/oidc/index'

import { tokenRefreshManager } from '../auth/tokenRefresh'

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean }

// generated client 會把 query 併入 url（如邀請碼、email），回報前只保留路徑
const toPathOnly = (url?: string): string => url?.split(/[?#]/)[0] ?? ''

// 回報 API 錯誤到 GlitchTip：4xx/5xx 與無回應（逾時/斷線）皆送
const reportApiError = (error: AxiosError, req?: RetryableRequestConfig) => {
  // 未 init（開發環境或未設 DSN）時 captureException 是 no-op，提前跳出以省下遮蔽與序列化
  if (!Sentry.getClient()) {
    return
  }

  // 使用者主動取消（如切換頁面、取消上傳）不是錯誤，不回報
  if (isCancel(error)) {
    return
  }

  // status 只在有 response 時才有值，故 undefined 即代表無回應
  const status = error.response?.status
  if (status !== undefined && status < 400) {
    return
  }

  // 5xx／無回應（斷線、逾時）屬非預期錯誤，維持 error；4xx 多為預期的業務錯誤，降級為 warning
  const isServerOrNetwork = status === undefined || status >= 500

  const path = toPathOnly(req?.url)

  Sentry.captureException(error, {
    level: isServerOrNetwork ? 'error' : 'warning',
    // 依 method＋端點＋status 分組，避免同一端點同 status 的 4xx 每筆各成一條 issue
    fingerprint: ['{{ default }}', req?.method?.toUpperCase() ?? '', path, String(status)],
    tags: {
      http_method: req?.method?.toUpperCase(),
      http_status: status,
      api_url: path
    },
    contexts: {
      api: {
        method: req?.method?.toUpperCase(),
        url: `${req?.baseURL ?? ''}${path}`,
        status,
        // 註冊/登入的 body 含密碼與頭像 base64，一律先遮蔽
        request_data: redactSensitive(req?.data),
        response_data: redactSensitive(error.response?.data)
      }
    }
  })
}

const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL: baseURL.replace(/\/+$/, ''),
    headers: {
      'Content-Type': 'application/json'
    }
  })

  instance.interceptors.request.use(
    (config) => {
      const token = tokenService.getAccessToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryableRequestConfig

      // 處理 401 Unauthorized 錯誤
      if (error.response?.status === HttpStatusCode.Unauthorized && !originalRequest._retry) {
        originalRequest._retry = true

        try {
          // refreshToken 內部已有佇列機制，會自動處理並發請求
          const newToken = await tokenRefreshManager.refreshToken()

          if (newToken) {
            return instance(originalRequest)
          }
        } catch {
          // 刷新失敗時，tokenRefreshManager 已處理登出邏輯
        }

        // refresh 失敗的持久性 401 屬 session 過期（預期事件），不回報
        return Promise.reject(error)
      }

      reportApiError(error, originalRequest)
      return Promise.reject(error)
    }
  )

  return instance
}

// 登入／token 交換不需帶 Authorization、也不做 401 refresh，但錯誤仍須回報
const createUnauthenticatedAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL: baseURL.replace(/\/+$/, '')
  })

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      reportApiError(error, error.config as RetryableRequestConfig)
      return Promise.reject(error)
    }
  )

  return instance
}

const aggrAxiosInstance = createAxiosInstance(import.meta.env.VITE_AGGR_BASE_URL)
const oidcAxiosInstance = createAxiosInstance(import.meta.env.VITE_OIDC_BASE_URL)
const simpleOidcAxiosInstance = createUnauthenticatedAxiosInstance(import.meta.env.VITE_OIDC_BASE_URL)

const createApiInstance = <T>(
  ApiClass: new (
    configuration: aggr.Configuration | oidc.Configuration,
    basePath: string | undefined,
    axiosInstance: AxiosInstance
  ) => T,
  config: aggr.Configuration | oidc.Configuration,
  axiosInstance: AxiosInstance
): T => {
  return new ApiClass(config, undefined, axiosInstance)
}

const createAggrApiInstance = <T>(
  ApiClass: new (configuration: aggr.Configuration, basePath: string | undefined, axiosInstance: AxiosInstance) => T
): T => {
  const config = new aggr.Configuration({
    basePath: import.meta.env.VITE_AGGR_BASE_URL.replace(/\/+$/, '')
  })
  return createApiInstance(ApiClass, config, aggrAxiosInstance)
}

const oidcConfiguration = new oidc.Configuration({
  basePath: import.meta.env.VITE_OIDC_BASE_URL.replace(/\/+$/, '')
})

const createOidcApiInstance = <T>(
  ApiClass: new (configuration: oidc.Configuration, basePath: string | undefined, axiosInstance: AxiosInstance) => T
): T => {
  return createApiInstance(ApiClass, oidcConfiguration, oidcAxiosInstance)
}

const createSimpleOidcApiInstance = <T>(
  ApiClass: new (configuration: oidc.Configuration, basePath: string | undefined, axiosInstance: AxiosInstance) => T
): T => {
  return createApiInstance(ApiClass, oidcConfiguration, simpleOidcAxiosInstance)
}

export { createAggrApiInstance, createOidcApiInstance, createSimpleOidcApiInstance }
